/**
 * @name useSensorsData
 * @description 数据埋点插件引入 & 设置
 */
import { ref, computed, watch } from '@vue/composition-api';
import { sensorsDataIdentity, sensorsDataEncryptionPubKey, sensorsDataShowLog } from '@/config/constant';
import sensorsDataMap from '@/config/SensorsDataMap/index';
import { isClient } from '@/libs/client';
import useStore from '@/mixins/useStore';
import useRouter from '@/mixins/useRouter';

export default () => {
    const { router } = useRouter();

    const { projectId, userModule, mainModule } = useStore();
    const {
        sysUserId,
        createdTime,
        sysRoleId,
        isExpired,
        packageText,
        packageType,
        balanceDay,
    } = userModule.useGetters([
        'sysUserId',
        'createdTime',
        'sysRoleId',
        'isExpired',
        'packageText',
        'packageType',
        'balanceDay',
    ]);
    const { local } = mainModule.useState(['local']);

    const SA_KEYS = Object.keys(sensorsDataMap);

    const packageTexts = {
        basic: 'Free版',
        professional: '专业版',
        commercial: '商业版',
        corporate: '定制版',
        pro: '高级版',
        vip: 'VIP版',
        enterprise: '企业版',
        pro_v1: 'Pro版',
        max: 'Max版',
    };

    const isError = ref(false);
    const loadSensorsJs = ref(false);
    const loadEncryptionJs = ref(false);
    const isInit = ref(false);

    const packageKeyText = computed(() => {
        // @ts-ignore
        return packageText.value ? (packageTexts[packageText.value] || '') : '';
    });

    const clientType = computed(() => {
        if (isClient()) {
            return 'd4';
        }

        return 'p1';
    });

    const clientExpandInfo = computed(() => {
        return {
            platform: clientType.value,
            platformVersion: (window as any).version,
        };
    });

    const initPageView = () => {
        const sensors = window.sensorsDataAnalytic201505;
        if (sensors && sensors.quick) {
            router.afterEach((to, from) => {
                console.log('useSensorsData router.afterEach');
                if (!to.meta.notPageView && to.name !== from.name && to.name !== 'idle') {
                    sensors.quick('autoTrackSinglePage');
                }
            });
        }
    };

    // 初始化
    const initSensorsData = () => {
        const sensors = window.sensorsDataAnalytic201505;
        if (!sensors) return;

        if (isInit.value) return;

        isInit.value = true;

        // 使用加密插件
        sensors.use('SMEncryption', {
            pkv: 3, // 加密标识
            pub_key: sensorsDataEncryptionPubKey, // 加密公钥
        });

        // Web JS SDK 全埋点包括三种事件：Web 页面浏览、Web 元素点击、Web 视区停留
        sensors.init({
            server_url: `https://wall-e.salesmartly.com/api/v1/pick-up?i=${sensorsDataIdentity}`, // 数据接收地址，i 为环境标识
            show_log: sensorsDataShowLog === '1', // 设置 true 后会在网页控制台打 logger，会显示发送的数据，设置 false 表示不显示，上线前请设置为 false
            encrypt_cookie: true, // 开启 cookie 加密配置，默认 false
            is_track_single_page: false, // 单页面配置，默认开启，若页面中有锚点设计，需要将该配置删除，否则触发锚点会多触发 $pageview 事件
            send_type: 'beacon', // 可选使用 'image' 图片 get 请求方式发数据。支持使用 'ajax' 和 'beacon' 方式发送数据，这两种默认都是 post 方式，beacon 方式兼容性较差，如果检测到浏览器不支持 beacon 时，会自动降级为使用 img 发送数据
            heatmap: {
                // 是否开启点击图，default 表示开启，自动采集 $WebClick 事件，可以设置 'not_collect' 表示关闭。
                // 默认只有点击 a input button textarea 四种元素时，才会触发 $WebClick 元素点击事件
                clickmap: 'default',
                // 此参数针对预置 $WebClick 事件（包括 quick('trackHeatMap') quick('trackAllHeatMap') 触发的）生效。
                // eslint-disable-next-line
                collect_element: function (element_target: any){
                    const _sa_key = element_target.getAttribute('data-sd-track');
                    if (SA_KEYS.includes(_sa_key)) {
                        return true;
                    }
                    return false;
                },
                // 此参数针对预置 $WebClick 事件（包括 quick('trackHeatMap') quick('trackAllHeatMap') 触发的）生效。
                // eslint-disable-next-line
                custom_property: function (element_target: any){
                    const _sa_key = element_target.getAttribute('data-sd-track');
                    if (SA_KEYS.includes(_sa_key)) {
                        return {
                            webClickCustomKey: _sa_key,
                            // @ts-ignore
                            webClickCustomName: sensorsDataMap[_sa_key],
                        };
                    }
                },
                // 是否开启触达图，not_collect 表示关闭，不会自动采集 $WebStay 事件，可以设置 'default' 表示开启。
                // 如果发生页面滚动，之前的页面停留是有效停留，也就是超过默认的 4 秒或者自定义的时间，javascript SDK 就会发送一次页面停留事件。
                // 关闭页面时，发送一次页面停留事件。神策 $WebStay 事件的采集是基于在 window 上绑定的 scroll 事件，如果页面滚动是绑定在 div 上，则无法采集。
                scroll_notice_map: 'not_collect',
            },
            // 子配置项 true 表示采集,false 表示不采集,未设置的参数取默认值
            preset_properties: {
                // 是否采集 $latest_utm 最近一次广告系列相关参数，默认值 true
                latest_utm: false,
                // 是否采集 $latest_traffic_source_type 最近一次流量来源类型，默认值 true
                latest_traffic_source_type: false,
                // 是否采集 $latest_search_keyword 最近一次搜索引擎关键字，默认值 true
                latest_search_keyword: false,
                // 是否采集 $latest_referrer 最近一次前向地址，默认值 true
                latest_referrer: false,
                // 是否采集 $latest_referrer_host 最近一次前向地址，默认是 false，需要手动设置为 true 开启
                latest_referrer_host: false,
                // 是否采集 $latest_landing_page 最近一次落地页地址，默认值 false
                latest_landing_page: false,
                // 是否采集 $url 页面地址作为公共属性，默认是 true
                url: true,
                // 是否采集 $title 页面标题作为公共属性，默认是 true
                title: true,
            },
        });

        // 注册公共属性
        sensors.registerPage({
            ...clientExpandInfo.value,
            // eslint-disable-next-line
            language: function () {
                return local.value || '';
            },
            // eslint-disable-next-line
            projectId: function () {
                return projectId.value || '';
            },
            // eslint-disable-next-line
            sysUserId: function () {
                return sysUserId.value || '';
            },
            // eslint-disable-next-line
            user_info: function () {
                return {
                    created_time: createdTime.value || '',
                    role_id: sysRoleId.value || '',
                    package: packageKeyText.value,
                    package_key: packageText.value || '',
                    package_type: packageType.value || '',
                    balance_day: balanceDay.value || '',
                    is_expired: isExpired.value || '',
                };
            },
        });

        // 传入自定义的属性插件对象给指定的事件添加、修改或删除属性
        sensors.registerPropertyPlugin({
            // eslint-disable-next-line
            properties: function (data: any) {
                if (data.event === '$WebClick') {
                    // 系统功能埋点特殊处理
                    if (data.properties.webClickCustomKey.indexOf('COMMON_') === 0) {
                        data.properties.$url = `${document.location.origin}/common`;
                        data.properties.$url_path = '/common';
                        data.properties.$title = 'common';
                        console.log('registerPropertyPlugin properties data', data);
                    }

                    // 删除不需要的属性
                    delete data.properties.$element_path;
                    delete data.properties.$element_selector;
                }
            },
        });

        /**
         * 设置之后，SDK 就会自动收集页面浏览事件 $pageview，以及设置初始来源
         * 调整至 router.afterEach 中，避免重复上报
         * 非登录页的上报
         */
        initPageView();
        // sensors.quick('autoTrack');
    };

    // load sdk
    const loadSensorsData = () => {
        const sensors = window.sensorsDataAnalytic201505;
        if (sensors) return;

        // 引入神策 SDK
        const sensorsJs: HTMLScriptElement = document.createElement('script');
        sensorsJs.src = 'https://client.salesmartly.com/js/sa-sdk/v1.26.19/sensorsdata.js';
        sensorsJs.charset = 'UTF-8';
        sensorsJs.async = true;
        document.body.appendChild(sensorsJs);

        // 引入加密插件
        const encryptionJs: HTMLScriptElement = document.createElement('script');
        // 加密插件的版本必须和 SDK 一致
        encryptionJs.src = 'https://client.salesmartly.com/js/sa-sdk/v1.26.19/plugin/sm-encryption/index.js';
        encryptionJs.charset = 'UTF-8';
        encryptionJs.async = true;
        document.body.appendChild(encryptionJs);

        sensorsJs.onload = () => {
            loadSensorsJs.value = true;
            if (loadSensorsJs.value && loadEncryptionJs.value && !isError.value) {
                initSensorsData();
            }
        };
        sensorsJs.onerror = (err: any) => {
            isError.value = true;
            loadSensorsJs.value = false;
            console.error('load sensorsJs error:', err);
        };

        encryptionJs.onload = () => {
            loadEncryptionJs.value = true;
            if (loadSensorsJs.value && loadEncryptionJs.value && !isError.value) {
                initSensorsData();
            }
        };
        encryptionJs.onerror = (err: any) => {
            isError.value = true;
            loadEncryptionJs.value = false;
            console.error('load encryptionJs error:', err);
        };
    };

    watch(sysUserId, (newVal) => {
        const sensors = window.sensorsDataAnalytic201505;
        if (sensors) {
            if (newVal) {
                console.log('useSensorsData login:', newVal);
                sensors.login(newVal);
            } else {
                console.log('useSensorsData logout');
                sensors.logout();
            }
        }
    }, { immediate: true });

    return {
        loadSensorsData,
    };
};
